import { Component, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core';
import { store } from '../../../redux/store';
import { isSameDay } from 'date-fns';
import { fetchDaysAvailability, fetchPossibleHours } from '../../../api';
import { dateToHourString, dateToString, priceWithMileagePerTime, wholeMonthDateRange } from '../../../utils/utils';
import { addItemToCart, fetchPrice, openCheckout, openReservation, requireInit } from '../../../redux/actions';
import { BookingDetails, Cart, ItemDetails, PriceOptions, TimeSlotPicker, Translations, Widget } from '../../../redux/types';
import { trans } from '../../../utils/trans';
import { RentwareCheckoutTemplate } from '../../rentware-checkout/rentware-checkout-modal/template/rentware-checkout-template';
import { ComponentView, DaysAvailability, Price } from '../../../types/types';

@Component({
  tag: 'rentware-article-booking-engine',
  shadow: false,
})
export class RentwareArticleBookingEngine {
  @Prop() articleId: string;
  @Prop() itemDetails: ItemDetails;
  @Prop() selectedVariationId?: string;
  @Prop() selectedMileageId?: string;
  @Prop() bookingDetails: BookingDetails;
  @Prop() widget: Widget;
  @Prop() timePicker: TimeSlotPicker;
  @Prop() locale: string;
  @Prop() cartState: Cart;
  @Prop() translations: Translations;
  @Prop() loadedComponents;
  @Prop() priceOptions: PriceOptions;
  @Prop() reservationPopup: ComponentView[];

  @State() loading: boolean = false;
  @State() error: boolean = false;
  @State() pickUpDate: Date;
  @State() pickUpTime: string;
  @State() pickUpDaysAvailability: DaysAvailability = {};
  @State() loadingPickUpDays: boolean = false;
  @State() loadingDropOffDays: boolean = false;
  @State() loadingPickUpHours: boolean = false;
  @State() loadingDropOffHours: boolean = false;
  @State() possibleHours: {} = {};
  @State() dropOffDate: Date;
  @State() dropOffTime: string;
  @State() dropOffDaysAvailability: DaysAvailability = {};
  @State() price: Price = { available: false, gross: 0, net: 0 };
  @State() amountToBook: number = 1;
  @State() firstAvailableDate: Date;
  @State() lastAvailableDate: Date;
  @State() timeSlotPicker: TimeSlotPicker;

  @Event() closeDateSelect: EventEmitter;

  openCheckout!: typeof openCheckout;
  addItemToCart!: typeof addItemToCart;
  requireInit!: typeof requireInit;
  fetchPrice!: typeof fetchPrice;
  errorTimer: ReturnType<typeof setTimeout>;
  openReservation!: typeof openReservation;

  componentWillLoad() {
    store.mapDispatchToProps(this, {
      addItemToCart,
      requireInit,
      openCheckout,
      fetchPrice,
      openReservation,
    });

    if (this.timePicker) {
      this.timeSlotPicker = {
        ...this.timePicker,
        onOptionChange: this.handleTimePickerOptionChange,
      };
    }
  }

  @Watch('orderDetailsState')
  watchOrderDetailsHandler(newValue) {
    //call if cart becomes empty
    if (!newValue || !newValue.id) {
      this.validateAndFetchPrice();
    }
  }

  disconnectedCallback() {
    clearInterval(this.errorTimer);
  }

  componentDidLoad() {
    this.requireInit();
  }

  componentWillRender() {
    const orderDetails = this.bookingDetails?.orderDetails;

    if (orderDetails) {
      const fromDateTime = new Date(orderDetails.fromDate);
      const toDateTime = new Date(orderDetails.toDate);

      if (!isSameDay(fromDateTime, this.pickUpDate)) {
        this.pickUpDate = new Date(fromDateTime);
      }
      if (!isSameDay(new Date(orderDetails.toDate), this.dropOffDate)) {
        this.dropOffDate = new Date(toDateTime);
      }
      if (dateToHourString(fromDateTime) !== this.pickUpTime) {
        this.pickUpTime = dateToHourString(fromDateTime);
      }
      if (dateToHourString(toDateTime) !== this.dropOffTime) {
        this.dropOffTime = dateToHourString(toDateTime);
      }
    }

    if (this.amountToBook < this.itemDetails?.minQuantity) {
      this.amountToBook = this.itemDetails.minQuantity;
    }
  }

  handleTimePickerOptionChange = value => {
    this.timeSlotPicker = { ...this.timeSlotPicker, selectedOption: value };
    this.validateAndFetchPrice();
  };

  handleAddToCart = () => {
    this.loading = true;
    const pickUpTime = this.withTime && !this.timeSlotPicker ? this.pickUpTime + ':00' : '12:00:00';
    const dropOffTime = this.withTime && this.dropOffTime && !this.timeSlotPicker ? this.dropOffTime + ':00' : '12:00:00';

    let variationsList = [];
    if (!!this.selectedVariationId) {
      variationsList = Array(this.amountToBook).fill(this.selectedVariationId);
    }

    this.addItemToCart(
      {
        itemId: this.articleId,
        fullDay: !this.withDateRange && !this.withTime,
        dateRange: {
          from: dateToString(this.pickUpDate) + 'T' + pickUpTime,
          to: dateToString(this.dropOffDate || this.pickUpDate) + 'T' + dropOffTime,
        },
        timeSlot: this.timeSlotPicker?.selectedOption,
        amountToAdd: this.amountToBook,
        variationsIds: variationsList,
        mileageOptionId: this.selectedMileageId,
        people: this.bookingDetails?.peopleNumber,
        price: this.price,
        name: this.itemDetails.name,
      },
      () => {
        const showReservation = this.reservationPopup?.includes(ComponentView.Calendar);

        this.hideError();
        if (showReservation) {
          this.openReservation(this.itemDetails.id);
        }
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.showErrorForTime(10000);
      },
    );
  };

  hideError = () => {
    clearInterval(this.errorTimer);
    this.error = false;
  };

  showErrorForTime = (time: number = 10000) => {
    this.error = true;

    clearInterval(this.errorTimer);
    this.errorTimer = setTimeout(() => {
      this.error = false;
    }, time);
  };

  validateAndFetchPrice(runWithLoading: boolean = true) {
    if (this.pickUpDate && (this.dropOffDate || this.dropOffTime || this.timeSlotPicker?.selectedOption)) {
      if (runWithLoading) {
        this.loading = true;
      }

      this.fetchPrice(
        this.articleId,
        this.selectedVariationId,
        {
          from: dateToString(this.pickUpDate),
          to: dateToString(this.dropOffDate || this.pickUpDate),
        },
        {
          from: this.pickUpTime,
          to: this.dropOffTime,
        },
        this.timeSlotPicker?.selectedOption,
        this.amountToBook,
        (data: any) => {
          this.price = {
            available: data.available,
            gross: data.price,
            net: data.netPrice,
            mileageOptions: data.mileageOptions,
            quantity: {
              min: data.minQuantity,
              max: data.maxQuantity,
              available: data.availableQuantity,
            },
          };
          this.priceOptions = { ...this.priceOptions, priceCalculation: data.priceCalculationType };

          this.loading = false;
        },
        () => {
          this.loading = false;
        },
      );
    } else {
      this.price = { available: false, gross: 0, net: 0, vat: 0, mileageOptions: [] };
    }
  }

  handleChangeAmountToBook = newAmount => {
    this.amountToBook = newAmount;
    this.validateAndFetchPrice(false);
  };

  handleVariationChange = (id: string) => {
    this.pickUpDate = null;
    this.dropOffDate = null;
    this.pickUpTime = '';
    this.dropOffTime = '';
    this.selectedVariationId = id;
    this.validateAndFetchPrice();
  };

  handleMileageChange = (id: string) => {
    this.selectedMileageId = id;
    this.validateAndFetchPrice();
  };

  handleOpenCheckout = () => {
    this.openCheckout();

    const intervalId = setInterval(() => {
      const checkoutLoaded = !!this.loadedComponents[RentwareCheckoutTemplate.name];

      if (!checkoutLoaded) {
        return;
      }

      clearInterval(intervalId);
    }, 100);
  };

  onDropOffTimeChange = value => {
    this.dropOffTime = value;
    this.validateAndFetchPrice();
  };

  onDropOffMonthChange = month => {
    this.loadingDropOffDays = true;
    fetchDaysAvailability(
      this.articleId,
      this.selectedVariationId,
      wholeMonthDateRange(month),
      'dropOff',
      this.pickUpDate ? dateToString(this.pickUpDate) : null,
      data => {
        this.dropOffDaysAvailability = data.days;
        this.firstAvailableDate = data.nearestAvailableDate ? new Date(data.nearestAvailableDate) : null;
        this.lastAvailableDate = data.lastAvailableDate ? new Date(data.lastAvailableDate) : null;
        this.loadingDropOffDays = false;
      },
      () => {
        this.loadingDropOffDays = false;
      },
      this.pickUpTime,
    );
  };

  onDropOffFocus = () => {
    this.loadingDropOffDays = true;
    fetchDaysAvailability(
      this.articleId,
      this.selectedVariationId,
      wholeMonthDateRange(this.dropOffDate ?? this.pickUpDate),
      'dropOff',
      this.pickUpDate ? dateToString(this.pickUpDate) : null,
      data => {
        this.dropOffDaysAvailability = data.days;
        this.firstAvailableDate = data.nearestAvailableDate ? new Date(data.nearestAvailableDate) : null;
        this.lastAvailableDate = data.lastAvailableDate ? new Date(data.lastAvailableDate) : null;
        this.loadingDropOffDays = false;
      },
      () => {
        this.loadingDropOffDays = false;
      },
      this.pickUpTime,
    );
  };

  onPickUpDayClick = date => {
    if (this.timeSlotPicker) {
      this.timeSlotPicker = { ...this.timeSlotPicker, selectedOption: null };
    }

    this.pickUpTime = '';
    this.dropOffTime = '';
    this.pickUpDate = date;

    if (!this.withDateRange) {
      this.dropOffDate = date;
    } else {
      this.dropOffDate = null;
    }

    this.price = { available: false, gross: 0, net: 0 };
    if (!this.withTime) {
      if (!this.withDateRange) {
        this.validateAndFetchPrice();
      }
    }
    this.loadingPickUpHours = true;

    if (!this.timeSlotPicker) {
      fetchPossibleHours(
        this.articleId,
        this.selectedVariationId,
        dateToString(this.pickUpDate),
        this.dropOffDate ? dateToString(this.dropOffDate) : null,
        (hours: any) => {
          this.possibleHours = hours;

          const pickupTimeObjectLength: number = Object.keys(this.possibleHours).length;
          if (pickupTimeObjectLength === 1) {
            const pickupHour = Object.keys(this.possibleHours)[0];
            this.onPickUpTimeChange(pickupHour);
            if (this.withDateRange) {
              this.closeDateSelect.emit();
            }

            if (!this.withDateRange) {
              const dropOffTimes = this.possibleHours[pickupHour];
              const dropOffTimeObjectLength: number = dropOffTimes.length;

              if (dropOffTimeObjectLength === 1) {
                this.onDropOffTimeChange(dropOffTimes[0]);
                this.closeDateSelect.emit();
              }
            }
          }
          this.loadingPickUpHours = false;
        },
        () => {
          this.loadingPickUpHours = false;
        },
      );
    }
  };

  onDropOffDayClick = date => {
    this.dropOffDate = date;
    this.price = { available: false, gross: 0, net: 0 };
    !this.withTime && this.validateAndFetchPrice();
    this.dropOffTime = null;
    this.loadingDropOffHours = true;

    fetchPossibleHours(
      this.articleId,
      this.selectedVariationId,
      dateToString(this.pickUpDate),
      dateToString(this.dropOffDate),
      (hours: any) => {
        this.possibleHours = hours;
        this.loadingDropOffHours = false;

        const dropOffTimeObjectLength: number = this.possibleHours[this.pickUpTime].length;
        if (dropOffTimeObjectLength === 1) {
          this.onDropOffTimeChange(this.possibleHours[this.pickUpTime][0]);
          this.closeDateSelect.emit();
        }
      },
      () => {
        this.loadingDropOffHours = false;
      },
    );
  };

  onPickUpFocus = () => {
    this.loadingPickUpDays = true;
    fetchDaysAvailability(
      this.articleId,
      this.selectedVariationId,
      wholeMonthDateRange(this.pickUpDate || new Date()),
      'pickUp',
      null,
      data => {
        this.pickUpDaysAvailability = data.days;
        this.firstAvailableDate = data.nearestAvailableDate ? new Date(data.nearestAvailableDate) : null;
        this.lastAvailableDate = null;
        this.loadingPickUpDays = false;
      },
      () => {
        this.loadingPickUpDays = false;
      },
    );
  };

  onPickUpMonthChange = date => {
    this.loadingPickUpDays = true;

    fetchDaysAvailability(
      this.articleId,
      this.selectedVariationId,
      wholeMonthDateRange(date),
      'pickUp',
      null,
      data => {
        this.pickUpDaysAvailability = data.days;
        this.firstAvailableDate = data.nearestAvailableDate ? new Date(data.nearestAvailableDate) : null;
        this.lastAvailableDate = null;
        this.loadingPickUpDays = false;
      },
      () => {
        this.loadingPickUpDays = false;
      },
    );
  };

  onPickUpTimeChange = value => {
    this.pickUpTime = value;
    if (this.withDateRange) {
      this.dropOffDate = null;
    }
    this.dropOffTime = null;

    const dropOffHours = this.possibleHours[this.pickUpTime];
    if (dropOffHours.length === 1) {
      this.onDropOffTimeChange(dropOffHours[0]);
    }
    this.validateAndFetchPrice();

    fetchPossibleHours(
      this.articleId,
      this.selectedVariationId,
      dateToString(this.pickUpDate),
      dateToString(this.dropOffDate || this.pickUpDate),
      (hours: any) => {
        this.possibleHours = hours;
        this.loadingDropOffHours = false;

        if (!this.withDateRange) {
          const dropOffTimeObjectLength: number = this.possibleHours[this.pickUpTime].length;
          if (dropOffTimeObjectLength === 1) {
            this.onDropOffTimeChange(this.possibleHours[this.pickUpTime][0]);
            this.closeDateSelect.emit();
          }
        }
      },
      () => {
        this.loadingDropOffHours = false;
      },
    );
  };

  handleResetCalendar = (month: Date) => {
    // reset full calendar
    this.pickUpDate = null;
    this.dropOffDate = null;
    this.pickUpTime = null;
    this.dropOffTime = null;
    this.onPickUpMonthChange(month);
  };

  get withTime(): boolean {
    return this.widget.timePickerEnabled;
  }

  get withDateRange(): boolean {
    return this.widget.kind !== 'date';
  }

  get orderReference() {
    return this.bookingDetails?.orderDetails?.id;
  }

  get notAvailable() {
    const dropOffTimeExist: boolean = this.withTime && !!this.dropOffTime;
    const dropOffDateExist: boolean = this.withDateRange && !this.withTime && !!this.dropOffDate;
    return !this.price.available && (dropOffTimeExist || dropOffDateExist);
  }

  get isThisItemInCart() {
    return this.cartState?.items.some(item => item.itemId === this.articleId);
  }

  get pickUpHours() {
    return Object.keys(this.possibleHours);
  }

  get dropOffHours() {
    return this.possibleHours[this.pickUpTime] || [];
  }

  get buttonText() {
    if (this.price.available) {
      const price = priceWithMileagePerTime(this.price, this.priceOptions, this.amountToBook, this.locale, this.translations, this.price.mileageOptions, this.selectedMileageId);

      return trans('articleBooking.addToCart', this.locale, this.translations, { amount: price });
    } else {
      if (this.timePicker) {
        if (!this.pickUpDate) {
          return trans('articleBooking.selectDateHint', this.locale, this.translations);
        } else {
          return trans('articleBooking.selectTimeSlotHint', this.locale, this.translations);
        }
      }

      return trans('articleBooking.selectDateHint', this.locale, this.translations);
    }
  }

  render() {
    const showMissingLocationError = !this.itemDetails.location && !this.bookingDetails?.cart;

    return (
      <Host class="CalendarTemplateContainerDimensions">
        {showMissingLocationError && <rentware-item-error message={'Article has no location assigned!'} hideClose={true} />}
        {this.error && <rentware-item-error onCloseClick={this.hideError} />}

        <rentware-item-template
          itemId={this.articleId}
          locale={this.locale}
          translations={this.translations}
          itemDetails={this.itemDetails}
          isThisItemInCart={this.isThisItemInCart}
          selectedVariationId={this.selectedVariationId}
          selectedMileageId={this.selectedMileageId}
          orderReference={this.orderReference}
          withTime={this.withTime}
          withDateRange={this.withDateRange}
          onPickUpFocus={this.onPickUpFocus}
          onDropOffFocus={this.onDropOffFocus}
          onVariationChange={this.handleVariationChange}
          loadingPickUpDays={this.loadingPickUpDays}
          loadingDropOffDays={this.loadingDropOffDays}
          loadingPickUpHours={this.loadingPickUpHours}
          loadingDropOffHours={this.loadingDropOffHours}
          notAvailable={this.notAvailable}
          notAvailableTitle={trans('articleBooking.notAvailableTitle', this.locale, this.translations)}
          notAvailableText={trans('articleBooking.notAvailableText', this.locale, this.translations)}
          firstAvailableDate={this.firstAvailableDate}
          lastAvailableDate={this.lastAvailableDate}
          loading={this.loading}
          price={this.price}
          timeSlotPicker={this.timeSlotPicker}
          emptyDatePlaceholder={trans('articleBooking.selectEmptyDatePlaceholder', this.locale, this.translations)}
          pickUpDate={this.pickUpDate}
          pickUpDaysAvailability={this.pickUpDaysAvailability}
          pickUpLabel={trans('articleBooking.pickUp', this.locale, this.translations)}
          pickUpTimeHint={trans('articleBooking.selectPickUpTimeHint', this.locale, this.translations)}
          pickUpTimePlaceholder={trans('articleBooking.selectPickUpTimePlaceholder', this.locale, this.translations)}
          pickUpTime={this.pickUpTime}
          pickUpHours={this.pickUpHours}
          onPickUpTimeChange={this.onPickUpTimeChange}
          onPickUpDayClick={this.onPickUpDayClick}
          onPickUpMonthChange={this.onPickUpMonthChange}
          dropOffDate={this.dropOffDate}
          dropOffHours={this.dropOffHours}
          dropOffLabel={trans('articleBooking.dropOff', this.locale, this.translations)}
          dropOffTimeHint={trans('articleBooking.selectDropOffTimeHint', this.locale, this.translations)}
          dropOffTimePlaceholder={trans('articleBooking.selectDropOffTimePlaceholder', this.locale, this.translations)}
          dropOffTime={this.dropOffTime}
          dropOffDaysAvailability={this.dropOffDaysAvailability}
          onDropOffTimeChange={this.onDropOffTimeChange}
          onDropOffDayClick={this.onDropOffDayClick}
          onDropOffMonthChange={this.onDropOffMonthChange}
          onMileageChange={this.handleMileageChange}
          headerTitle={trans('articleBooking.title', this.locale, this.translations)}
          backToArticleText={trans('articleBooking.backToArticle', this.locale, this.translations)}
          buttonText={this.buttonText}
          checkoutButtonText={trans('cart.buttonOpenCheckout', this.locale, this.translations)}
          onSubmit={this.handleAddToCart}
          onChangeAmountToBook={this.handleChangeAmountToBook}
          amountToAdd={this.amountToBook}
          onCheckoutClick={this.handleOpenCheckout}
          onResetCalendar={this.handleResetCalendar}
        />
      </Host>
    );
  }
}
